/**
 * IBPathwayExplorerPage — API-driven, SEO-enriched, deep-linkable.
 * Routes:
 *   /ib                                       → root explorer (default faculty)
 *   /ib/:faculty                              → faculty landing
 *   /ib/:faculty/:stream                      → stream landing
 *   /ib/:faculty/:stream/:programmeSlug       → programme detail (university list)
 * Legacy /ib-pathway-explorer also routes here for backward compat.
 */
import { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import SEO from '@/components/SEO';
import {
  ArrowRight, ArrowLeft, ChevronRight, Sparkles, MessageCircle,
  GraduationCap, Clock, Award, IndianRupee, Loader2,
} from 'lucide-react';
import { LeadCaptureModal } from '@/components/ib/LeadCaptureModal';

const API = process.env.REACT_APP_BACKEND_URL || '';
const SITE = 'https://unixperts.com';

// Tailwind-safe colour map (faculty colour → pill tones). Mirrors the data colour ids.
const FAC_TONE = {
  purple: { pill: '#EEEDFE', pillT: '#3C3489', badge: '#534AB7', border: '#AFA9EC' },
  coral:  { pill: '#FAECE7', pillT: '#712B13', badge: '#D85A30', border: '#F0997B' },
  blue:   { pill: '#E6F1FB', pillT: '#0C447C', badge: '#185FA5', border: '#85B7EB' },
  amber:  { pill: '#FAEEDA', pillT: '#633806', badge: '#BA7517', border: '#FAC775' },
  teal:   { pill: '#E1F5EE', pillT: '#085041', badge: '#0F6E56', border: '#5DCAA5' },
  green:  { pill: '#EAF3DE', pillT: '#27500A', badge: '#3B6D11', border: '#97C459' },
  pink:   { pill: '#FBEAF0', pillT: '#4B1528', badge: '#993556', border: '#ED93B1' },
};
const tone = (c) => FAC_TONE[c] || FAC_TONE.purple;

const COUNTRY_FLAG = { AU: '🇦🇺', UK: '🇬🇧', CA: '🇨🇦', NZ: '🇳🇿', US: '🇺🇸' };
const COUNTRY_NAME = { AU: 'Australia', UK: 'UK', CA: 'Canada', NZ: 'New Zealand', US: 'USA' };
const CC = {
  AU: { bg: '#E1F5EE', fg: '#085041' }, UK: { bg: '#E6F1FB', fg: '#0C447C' },
  CA: { bg: '#FAEEDA', fg: '#633806' }, NZ: { bg: '#EEEDFE', fg: '#3C3489' },
  US: { bg: '#EAF3DE', fg: '#27500A' },
};

const slugify = (s) =>
  (s || '').toLowerCase().replace(/[^a-z0-9\s-]/g, '').trim().replace(/[\s_-]+/g, '-').slice(0, 80);

// ───────────────────────────────────────────────────────────────────────────
// Top-level component
// ───────────────────────────────────────────────────────────────────────────
const IBPathwayExplorerPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const facultySlug = params.faculty || 'psychology';
  const streamSid = params.stream || null;
  const programmeSlug = params.programmeSlug || null;

  const [faculties, setFaculties] = useState([]);
  const [bundle, setBundle] = useState(null); // {faculty, streams}
  const [loadingBundle, setLoadingBundle] = useState(true);

  const [leadOpen, setLeadOpen] = useState(false);
  const [leadCtx, setLeadCtx] = useState({});

  // Load faculty list once
  useEffect(() => {
    fetch(`${API}/api/ib/faculties`).then(r => r.ok ? r.json() : []).then(setFaculties);
  }, []);

  // Load faculty bundle on slug change
  useEffect(() => {
    setLoadingBundle(true);
    fetch(`${API}/api/ib/faculty/${facultySlug}`)
      .then(r => r.ok ? r.json() : null)
      .then(setBundle)
      .finally(() => setLoadingBundle(false));
  }, [facultySlug]);

  const fac = bundle?.faculty;
  const streams = bundle?.streams || [];
  const facTone = useMemo(() => tone(fac?.color), [fac?.color]);

  const activeStream = useMemo(
    () => streamSid ? streams.find(s => s.sid === streamSid) : null,
    [streams, streamSid]
  );
  const programmeMatch = useMemo(() => {
    if (!activeStream || !programmeSlug) return null;
    // programmeSlug format = "{slug}--{idx}" so we can disambiguate duplicates
    const [s, idxStr] = programmeSlug.split('--');
    const idx = parseInt(idxStr, 10);
    const prog = (activeStream.programs || [])[idx];
    if (!prog) return null;
    if (slugify(prog.name) !== s) return null;
    return { prog, idx };
  }, [activeStream, programmeSlug]);

  const openLead = (ctx = {}) => { setLeadCtx({ facultyName: fac?.label, ...ctx }); setLeadOpen(true); };

  // ─── Navigation handlers ────────────────────────────────────────────────
  const goFaculty = (slug) => navigate(`/ib/${slug}`);
  const goStream = (sid) => navigate(`/ib/${facultySlug}/${sid}`);
  const goProgramme = (sid, idx, name) => navigate(`/ib/${facultySlug}/${sid}/${slugify(name)}--${idx}`);

  // ─── SEO bundles ────────────────────────────────────────────────────────
  const seo = useMemo(() => {
    if (programmeMatch && fac && activeStream) {
      const p = programmeMatch.prog;
      return {
        title: `${p.name} — ${activeStream.title}`,
        description: `Universities offering ${p.name} for IB students — ${activeStream.title} in ${fac.label}. Compare entry requirements, fees, accreditation across ${(p.countries || []).map(c => COUNTRY_NAME[c]).join(', ')}.`,
        canonical: `${SITE}/ib/${facultySlug}/${streamSid}/${programmeSlug}`,
        type: 'article',
        structuredData: {
          '@context': 'https://schema.org',
          '@type': 'Course',
          name: p.name,
          description: p.note,
          provider: { '@type': 'Organization', name: 'UniXperts', url: SITE },
          educationalLevel: 'Undergraduate',
          about: activeStream.title,
        },
      };
    }
    if (activeStream && fac) {
      return {
        title: `${activeStream.title} — ${fac.label} for IB Students`,
        description: `${activeStream.sub}. ${activeStream.ibFit?.slice(0, 140) || ''}`,
        canonical: `${SITE}/ib/${facultySlug}/${streamSid}`,
      };
    }
    if (fac) {
      return {
        title: `${fac.label} Abroad for IB Students — Universities, Fees, Careers`,
        description: (fac.ibNote || '').slice(0, 180),
        canonical: `${SITE}/ib/${facultySlug}`,
      };
    }
    return {
      title: 'IB Pathway Explorer — Universities, Fees & Careers',
      description: '10 faculties · 54+ specialisations · 200+ universities across 5 destinations. Filter by IB combination, compare fees & accreditation, and see careers on return to India.',
      canonical: `${SITE}/ib`,
    };
  }, [fac, activeStream, programmeMatch, facultySlug, streamSid, programmeSlug]);

  // ─── Renderers ──────────────────────────────────────────────────────────
  const renderFacultyTabs = () => (
    <div className="flex gap-2 flex-wrap mb-8" data-testid="ib-faculty-tabs">
      {faculties.map(f => {
        const active = f.slug === facultySlug;
        const c = tone(f.color);
        return (
          <Link
            key={f.slug} to={`/ib/${f.slug}`}
            data-testid={`ib-faculty-${f.slug}`}
            className={`px-4 py-1.5 text-sm rounded-full transition-all border ${active ? 'shadow-sm font-bold' : 'border-gray-200 dark:border-slate-700 text-gray-600 dark:text-gray-300 hover:border-gray-300'}`}
            style={active ? { background: c.pill, color: c.pillT, borderColor: c.badge } : undefined}
          >
            <span className="mr-1.5">{f.icon}</span>{f.label}
          </Link>
        );
      })}
    </div>
  );

  const renderProgrammeDetail = () => {
    const p = programmeMatch.prog;
    const [unis, setUnis] = [null, null]; // placeholder; actual state below
    return <ProgrammeDetail
      faculty={fac} stream={activeStream} programme={p} idx={programmeMatch.idx}
      tone={facTone} onBack={() => navigate(`/ib/${facultySlug}/${streamSid}`)}
      onLead={openLead}
    />;
  };

  const renderStream = () => (
    <StreamPanel
      faculty={fac} stream={activeStream} streams={streams}
      onBack={() => navigate(`/ib/${facultySlug}`)}
      onProgramme={(idx, name) => goProgramme(activeStream.sid, idx, name)}
      onLead={openLead}
    />
  );

  return (
    <>
      <SEO {...seo} />
      <Header />
      <main className="min-h-screen bg-gradient-to-b from-white to-gray-50 dark:from-slate-950 dark:to-slate-900 pb-24" data-testid="ib-pathway-explorer">
        <section className="max-w-5xl mx-auto px-4 pt-10 md:pt-16 pb-8 text-center">
          <Badge className="bg-ux-cyan/10 text-ux-cyan hover:bg-ux-cyan/10 border-0 inline-flex items-center gap-1.5 mb-4">
            <Sparkles className="w-3 h-3" /> Built for IB students
          </Badge>
          <h1 className="text-4xl md:text-5xl font-bold leading-tight tracking-tight">
            The IB Pathway Explorer
          </h1>
          <p className="text-base md:text-lg text-gray-500 mt-4 max-w-2xl mx-auto">
            From your IB subjects, all the way to a university and a career — across 10 faculties and 5 destinations.
            Built by UniXperts counsellors. Free to use.
          </p>
        </section>

        {/* Breadcrumb */}
        {fac && (
          <nav className="max-w-5xl mx-auto px-4 mb-3 text-xs text-gray-500" aria-label="Breadcrumb" data-testid="ib-breadcrumb">
            <Link to="/ib" className="hover:text-ux-cyan">IB Pathway Explorer</Link>
            <span className="mx-1">/</span>
            <Link to={`/ib/${facultySlug}`} className="hover:text-ux-cyan">{fac.label}</Link>
            {activeStream && (<>
              <span className="mx-1">/</span>
              <Link to={`/ib/${facultySlug}/${streamSid}`} className="hover:text-ux-cyan">{activeStream.title}</Link>
            </>)}
            {programmeMatch && (<>
              <span className="mx-1">/</span>
              <span className="text-foreground font-semibold">{programmeMatch.prog.name}</span>
            </>)}
          </nav>
        )}

        <div className="max-w-5xl mx-auto px-4">
          {!programmeMatch && !activeStream && renderFacultyTabs()}

          {loadingBundle ? (
            <div className="py-24 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-ux-cyan" /></div>
          ) : !fac ? (
            <Card className="p-12 text-center">
              <p>Faculty not found.</p>
              <Button asChild className="mt-4 bg-ux-cyan hover:bg-ux-cyan/90"><Link to="/ib">Back to Explorer</Link></Button>
            </Card>
          ) : programmeMatch ? (
            renderProgrammeDetail()
          ) : activeStream ? (
            renderStream()
          ) : (
            <FacultyLanding
              faculty={fac} streams={streams} tone={facTone}
              onStream={goStream} onLead={openLead}
            />
          )}
        </div>

        {/* Sticky CTA */}
        <button
          type="button" onClick={() => openLead({})} data-testid="ib-sticky-cta"
          className="fixed bottom-5 right-5 z-40 bg-ux-cyan hover:bg-ux-cyan/90 text-white shadow-2xl shadow-ux-cyan/30 rounded-full pl-3 pr-4 py-2.5 flex items-center gap-2 text-sm font-bold transition-all hover:scale-105"
        >
          <span className="bg-white/20 rounded-full p-1.5"><MessageCircle className="w-3.5 h-3.5" /></span>
          Talk to a counsellor
        </button>
        <LeadCaptureModal open={leadOpen} onOpenChange={setLeadOpen} ctx={leadCtx} />
      </main>
      <Footer />
    </>
  );
};

// ─── Faculty landing (streams list) ──────────────────────────────────────────
const FacultyLanding = ({ faculty, streams, tone: facTone, onStream, onLead }) => (
  <>
    <Card className="mb-6 border-l-[3px]" style={{ borderLeftColor: facTone.badge }}>
      <CardContent className="p-5">
        <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-2">IB lens — {faculty.label}</p>
        <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{faculty.ibNote}</p>
      </CardContent>
    </Card>
    <div className="space-y-3" data-testid="ib-streams">
      {streams.map(s => {
        const sc = tone(s.fitColor);
        return (
          <button
            key={s.sid} onClick={() => onStream(s.sid)} data-testid={`ib-stream-${s.sid}`}
            className="w-full text-left rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-4 sm:px-5 flex items-center justify-between gap-3 hover:border-gray-300 dark:hover:border-slate-600 hover:shadow-sm transition-all"
          >
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <div className="text-2xl flex-shrink-0">{s.icon}</div>
              <div className="min-w-0 flex-1">
                <h3 className="font-bold leading-tight truncate">{s.title}</h3>
                <p className="text-xs text-gray-500 truncate">{s.sub}</p>
              </div>
            </div>
            <span className="text-[11px] font-bold rounded-full px-2.5 py-0.5 flex-shrink-0 whitespace-nowrap" style={{ background: sc.pill, color: sc.pillT }}>
              {(s.programs || []).length} programmes
            </span>
          </button>
        );
      })}
    </div>
    <InlineLeadCard onClick={() => onLead({})} testid="ib-faculty-bottom-cta"
      label={`Not sure which ${faculty.label} stream fits your IB profile?`}
      sub="Share your IB subjects and predicted scores — we'll map them to your best-fit specialisation."
    />
    {faculty.ibCombos?.length > 0 && (
      <div className="mt-8">
        <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-3">Ideal IB subject combinations — {faculty.label}</p>
        <div className="flex flex-wrap gap-2">
          {faculty.ibCombos.map((c, i) => (
            <span key={i} className="text-xs bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg px-3 py-1.5 text-gray-600 dark:text-gray-300">{c}</span>
          ))}
        </div>
      </div>
    )}
  </>
);

// ─── Stream panel (programmes list + careers) ────────────────────────────────
const StreamPanel = ({ faculty, stream, onBack, onProgramme, onLead }) => {
  const sc = tone(stream.fitColor);
  return (
    <>
      <Button variant="ghost" size="sm" onClick={onBack} className="mb-4 -ml-2" data-testid="ib-stream-back">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to {faculty.label}
      </Button>
      <div className="mb-6">
        <span className="text-3xl">{stream.icon}</span>
        <h2 className="text-3xl font-bold mt-2">{stream.title}</h2>
        <p className="text-sm text-gray-500">{stream.sub}</p>
      </div>
      <div className="rounded-lg p-3.5 mb-6 text-xs leading-relaxed" style={{ background: sc.pill, color: sc.pillT }}>
        <span className="font-bold">IB fit · </span>{stream.ibFit}
      </div>
      <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-2.5">Undergraduate programmes</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5 mb-8">
        {(stream.programs || []).map((p, idx) => (
          <button key={idx} onClick={() => onProgramme(idx, p.name)}
            data-testid={`ib-prog-${stream.sid}-${idx}`}
            className="text-left rounded-xl border border-gray-200 dark:border-slate-700 p-3.5 hover:border-gray-300 hover:shadow-sm transition-all min-w-0">
            <p className="font-bold text-sm leading-tight break-words whitespace-normal">{p.name}</p>
            <p className="text-xs text-gray-500 mt-1 leading-relaxed break-words whitespace-normal">{p.note}</p>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-3">
              <div className="flex gap-1 flex-wrap min-w-0">
                {(p.countries || []).map(cc => (
                  <span key={cc} className="text-[10px] font-bold rounded-full px-2 py-0.5 whitespace-nowrap"
                    style={{ background: CC[cc]?.bg, color: CC[cc]?.fg }}>
                    {COUNTRY_FLAG[cc]} {cc}
                  </span>
                ))}
              </div>
              <span className="text-[11px] font-bold whitespace-nowrap flex-shrink-0 self-end sm:self-auto" style={{ color: sc.badge }}>View universities →</span>
            </div>
          </button>
        ))}
      </div>
      <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-2">Career directions</p>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {(stream.careers || []).map((c, i) => (
          <span key={i} className="text-xs bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-full px-2.5 py-0.5 text-gray-600 dark:text-gray-300">{c}</span>
        ))}
      </div>
      <div className="rounded-lg bg-gray-50 dark:bg-slate-800/60 p-3 text-xs text-gray-600 dark:text-gray-300 leading-relaxed mb-6">
        {stream.careersNote}
      </div>
      <InlineLeadCard onClick={() => onLead({ streamName: stream.title })}
        label={`Talk to a counsellor about ${stream.title}`}
        sub="Personalised university shortlist + IB combo advice + India-return ROI in one 30-min call — free."
        testid={`ib-stream-cta-${stream.sid}`} />
    </>
  );
};

// ─── Programme detail (universities) ─────────────────────────────────────────
const ProgrammeDetail = ({ faculty, stream, programme, idx, tone: facTone, onBack, onLead }) => {
  const [unis, setUnis] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/ib/universities?faculty=${faculty.slug}&stream=${stream.sid}&program_index=${idx}`)
      .then(r => r.ok ? r.json() : [])
      .then(setUnis).finally(() => setLoading(false));
  }, [faculty.slug, stream.sid, idx]);

  return (
    <>
      <Button variant="ghost" size="sm" onClick={onBack} className="mb-4 -ml-2" data-testid="ib-prog-back">
        <ArrowLeft className="w-4 h-4 mr-1" /> Back to {stream.title}
      </Button>
      <div className="mb-8">
        <Badge style={{ background: facTone.pill, color: facTone.pillT }} className="border-0">Programme</Badge>
        <h1 className="text-3xl md:text-4xl font-bold mt-3 leading-tight">{programme.name}</h1>
        <p className="text-sm text-gray-500 mt-2">{unis.length} {unis.length === 1 ? 'university' : 'universities'} matched · ranked by global standing</p>
      </div>
      {loading ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-7 h-7 animate-spin text-ux-cyan" /></div>
      ) : unis.length === 0 ? (
        <Card className="p-10 text-center">
          <p className="text-gray-500 mb-4">Detailed university data is still being curated for this programme.</p>
          <Button onClick={() => onLead({ streamName: stream.title, programmeName: programme.name })} className="bg-ux-cyan hover:bg-ux-cyan/90">
            Get this list as a personalised report <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {unis.map((u, i) => (
            <Card key={u.id || i} className="overflow-hidden border-l-[3px] hover:shadow-lg transition-shadow"
              style={{ borderLeftColor: facTone.badge }} data-testid={`ib-uni-card-${i}`}>
              <div className="px-5 py-2.5 flex items-center justify-between"
                style={{ background: CC[u.country]?.bg, color: CC[u.country]?.fg }}>
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
                  <span className="text-base">{COUNTRY_FLAG[u.country]}</span>{COUNTRY_NAME[u.country]}
                </div>
                <span className="text-xs font-bold rounded-full px-2.5 py-0.5" style={{ background: facTone.badge, color: '#fff' }}>{u.rank}</span>
              </div>
              <CardContent className="p-5 space-y-4">
                <div>
                  <h3 className="font-bold text-lg leading-tight">{u.name}</h3>
                  <p className="text-sm text-gray-500 mt-0.5">{u.degree}</p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {[
                    { label: 'Annual intl fee', val: u.fee, sub: u.feeUnit, Icon: IndianRupee },
                    { label: 'Duration', val: u.duration, Icon: Clock },
                    { label: 'IB requirement', val: u.ib, Icon: GraduationCap },
                    { label: 'Accreditation', val: u.accred, Icon: Award },
                  ].map(s => (
                    <div key={s.label} className="bg-gray-50 dark:bg-slate-800/60 rounded-lg p-2.5">
                      <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1 flex items-center gap-1">
                        <s.Icon className="w-3 h-3" />{s.label}
                      </div>
                      <div className="text-xs font-bold leading-snug">{s.val}</div>
                      {s.sub && <div className="text-[10px] text-gray-400">{s.sub}</div>}
                    </div>
                  ))}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed pl-3 border-l-2" style={{ borderColor: facTone.border }}>{u.brief}</p>
                <div className="flex flex-wrap items-center justify-between gap-3 pt-1">
                  <p className="text-[11px] text-gray-400">Ranking source: {u.rankLabel}</p>
                  <Button size="sm" onClick={() => onLead({ streamName: stream.title, programmeName: programme.name, universityName: u.name, country: COUNTRY_NAME[u.country] })}
                    className="bg-ux-cyan hover:bg-ux-cyan/90 rounded-full" data-testid={`ib-uni-cta-${i}`}>
                    <MessageCircle className="w-3.5 h-3.5 mr-1.5" />Get personalised guidance
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </>
  );
};

// ─── Inline lead card ────────────────────────────────────────────────────────
const InlineLeadCard = ({ onClick, label, sub, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className="w-full text-left rounded-2xl border border-ux-cyan/30 bg-gradient-to-r from-ux-cyan/5 via-ux-cyan/[0.03] to-transparent p-5 flex flex-wrap items-center justify-between gap-3 hover:border-ux-cyan/60 hover:shadow-md transition-all group mt-8">
    <div className="min-w-0">
      <p className="font-bold text-sm flex items-center gap-1.5 whitespace-normal break-words"><Sparkles className="w-3.5 h-3.5 text-ux-cyan flex-shrink-0" />{label}</p>
      <p className="text-xs text-gray-500 mt-0.5 whitespace-normal break-words">{sub}</p>
    </div>
    <span className="inline-flex items-center gap-1 text-xs font-semibold text-ux-cyan group-hover:translate-x-0.5 transition-transform">
      Book a free call <ChevronRight className="w-3.5 h-3.5" />
    </span>
  </button>
);

export default IBPathwayExplorerPage;
